import { Component, EventEmitter, Input, ViewChild } from '@angular/core';
import { ModalAction } from '../../models';
import { ReportModalService } from './report-modal.service';
import { ReportInputModel, ReportSurveyTypes } from './report.model';
import { isNullOrUndefined } from 'util';
import { Subscription } from 'rxjs';
import { UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { handleErrors } from '../../common/error';
import { RecaptchaComponent } from 'ng-recaptcha';
import { AppSettings } from '../../common/config';
import { EmailValidator } from '../../validators/emailValidator';
import { LeadingTrailingWhiteSpaceValidator } from '../../validators/leadingTrailingWhiteSpaceValidator';
import { ToastrService } from 'ngx-toastr';

/** Example Reports **/
const exampleReportWithQuestionKey: ReportInputModel = {
orgName: 'Smart Response',

  surveyId: 1,

  surveyType: ReportSurveyTypes.GOB,

  surveyQuestionKey: 'govIdOrEin',
  surveyQuestionTranslationKey: 'CommonGOBSurvey-Q_ORG_ID',
};

const exampleReportEntireSurvey: ReportInputModel = {
  orgName: 'Smart Response',

  surveyId: 1,

  surveyType: ReportSurveyTypes.GOB,
};

@Component({
  selector: 'report-modal',
  providers: [ReportModalService],
  templateUrl: './report-modal.component.html',
  styleUrls: ['./report-modal.component.css', '../common-search.css'],
})
export class ReportModalComponent {
  @ViewChild(RecaptchaComponent, { static: false }) captcha: RecaptchaComponent;

  @Input()
  public set reportActions(eventEmitter: EventEmitter<ReportInputModel>) {
    if (isNullOrUndefined(eventEmitter)) {
      return;
    }

    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    this.subscription = eventEmitter.subscribe((next) => {
      if (isNullOrUndefined(next)) {
        return;
      }

      this.reportInputModel = next;

      this.resetForm();

      this.modalActions.emit({ action: 'modal', params: ['open'] });
    });
  }

  public modalActions = new EventEmitter<string | ModalAction>();
  public reportModalForm: UntypedFormGroup;
  public isSubmitting = false;

  private subscription: Subscription;
  public reportInputModel: ReportInputModel;

  public AppSettings = AppSettings;
  readonly animationDuration = 250;

  constructor(
    private translateService: TranslateService,
    private toastr: ToastrService,
    private reportModalService: ReportModalService
  ) {
    this.resetForm();
  }

  resetForm() {
    if (this.captcha) {
      this.captcha.reset();
    }
    if (this.reportInputModel) {
      const formGroup = {
        captcha: new UntypedFormControl('', Validators.required),
        reason: new UntypedFormControl('', [Validators.required, Validators.maxLength(256)]),
        surveyId: new UntypedFormControl(this.reportInputModel.surveyId, Validators.required),
        surveyType: new UntypedFormControl(this.reportInputModel.surveyType, Validators.required),
        redFlagReportRespondentEmail: new UntypedFormControl('', [
          Validators.maxLength(64),
          EmailValidator.mailFormat,
          LeadingTrailingWhiteSpaceValidator.leadingTrailingWhiteSpaceFormat,
        ]),
        redFlagReportRespondentName: new UntypedFormControl('', [
          Validators.maxLength(32),
          LeadingTrailingWhiteSpaceValidator.leadingTrailingWhiteSpaceFormat,
        ]),
        redFlagReportRespondentPhone: new UntypedFormControl('', [
          Validators.maxLength(32),
          LeadingTrailingWhiteSpaceValidator.leadingTrailingWhiteSpaceFormat,
        ]),
        redFlagReportRespondentRole: new UntypedFormControl(),
        redFlagReportRespondentComment: new UntypedFormControl('', [
          Validators.maxLength(256),
          LeadingTrailingWhiteSpaceValidator.leadingTrailingWhiteSpaceFormat,
        ]),
        reportDate: new UntypedFormControl(),
      };

      if (this.reportInputModel.surveyQuestionKey) {
        formGroup['surveyQuestionKey'] = new UntypedFormControl(this.reportInputModel.surveyQuestionKey);
      }

      this.reportModalForm = new UntypedFormGroup(formGroup);

      this.reportInputModel.surveyTypeTranslationKey = ReportSurveyTypes.toTranslationKey(
        this.reportInputModel.surveyType
      );
    } else {
      this.reportModalForm = new UntypedFormGroup({
        captcha: new UntypedFormControl('', Validators.required),
        reason: new UntypedFormControl('', Validators.required),
        surveyId: new UntypedFormControl('', Validators.required),
        redFlagReportRespondentEmail: new UntypedFormControl(''),
        redFlagReportRespondentName: new UntypedFormControl(''),
        redFlagReportRespondentPhone: new UntypedFormControl(''),
        redFlagReportRespondentRole: new UntypedFormControl(),
        redFlagReportRespondentComment: new UntypedFormControl('', [
          Validators.maxLength(256),
          LeadingTrailingWhiteSpaceValidator.leadingTrailingWhiteSpaceFormat,
        ]),
        reportDate: new UntypedFormControl(),
      });
    }
  }

  onValidCaptchaResponse(captcha: string) {
    this.reportModalForm.controls['captcha'].setValue(captcha);
  }

  onSubmit() {
    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    const date = new Date().getTime();
    this.reportModalForm.patchValue({ reportDate: date });
    this.reportModalService.submitReport(this.reportModalForm.value).subscribe(
      (success) => {
        this.isSubmitting = false;
        this.toastr.success(
          this.translateService.instant('ReportModalComponent-SUBMIT_SUCCESS'),
          this.translateService.instant('ReportModalComponent-TITLE'),
          {
            timeOut: 5000,
          }
        );

        this.modalActions.emit({ action: 'modal', params: ['close'] });

        this.reportInputModel = null;

        this.resetForm();
      },
      (error) => {
        this.isSubmitting = false;

        handleErrors(error, this.translateService, this.toastr, 'ReportModalComponent-TITLE');
      }
    );
  }
}
